/**
 * /app/:tenantSlug/holidays — Public-holiday calendar admin (full CRUD).
 *
 * Batch G2 — the full admin surface for managing the tenant's public-
 * holiday calendar. HR admins (company_admin / hr_manager) can add,
 * edit, and soft-delete holidays. All other authenticated users see a
 * read-only list.
 *
 * URL: /app/:tenantSlug/holidays
 *   (linked from Settings → Holidays → "Open calendar" button)
 *
 * Soft-delete semantics: DELETE /api/v1/holidays/{id} flips is_active=false.
 * Historical leave records counted against the holiday remain stable. The
 * default list shows only active holidays; "Show inactive" toggle surfaces
 * the full set.
 *
 * Migration note (2026-06-01, Next.js → Vite): replaces the old redirect
 * stub that sent users back to Settings → Holidays. The full CRUD was
 * previously only in the Next.js app (Batch G2); this is the Vite SPA port.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { useHolidays } from "@/hooks/use-holidays";
import { HolidayFormDialog } from "@/components/settings/holiday-form-dialog";
import { HolidayDeleteButton } from "@/components/settings/holiday-delete-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toApiError } from "@/lib/api";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/holidays")({
  component: HolidaysPage,
});

/** Format a YYYY-MM-DD date string as "DD MMM YYYY" (en-IN, e.g. "25 Dec 2026"). */
function fmtHolidayDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function HolidaysPage() {
  const { isHrAdmin } = useHrRoles();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const years = useMemo(
    () => [currentYear - 1, currentYear, currentYear + 1, currentYear + 2],
    [currentYear],
  );

  const q = useHolidays({ year, include_inactive: includeInactive });

  const holidays = useMemo(
    () =>
      [...(q.data?.items ?? [])].sort(
        (a, b) => a.holiday_date.localeCompare(b.holiday_date),
      ),
    [q.data],
  );

  return (
    <div className="space-y-6 p-6">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Public holidays
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage the public-holiday calendar for this company. Holidays
            are excluded from leave day-counts and timesheet non-working-day
            calculations.
          </p>
        </div>
        {isHrAdmin && (
          <>
            {/* Controlled dialog — trigger lives both here and in the empty state */}
            <HolidayFormDialog
              open={addOpen}
              onOpenChange={setAddOpen}
              showTrigger
            />
          </>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="holidays-year">Year</Label>
          <Select
            value={String(year)}
            onValueChange={(v) => setYear(Number(v))}
          >
            <SelectTrigger id="holidays-year" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isHrAdmin && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input accent-primary"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />{" "}
            Show inactive
          </label>
        )}
      </div>

      {/* Loading */}
      {q.isLoading && (
        <div className="space-y-3">
          {["s1", "s2", "s3"].map((k) => (
            <Skeleton key={k} className="h-12 w-full" />
          ))}
        </div>
      )}

      {/* Error */}
      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t load holidays</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {/* Empty state */}
      {q.data && holidays.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No holidays for {year}
            </p>
            {isHrAdmin && (
              <Button
                variant="outline"
                className="mt-4 gap-1.5"
                onClick={() => setAddOpen(true)}
              >
                Add holiday
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {q.data && holidays.length > 0 && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                {isHrAdmin && (
                  <TableHead className="text-right">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {holidays.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="font-mono text-sm whitespace-nowrap">
                    {fmtHolidayDate(h.holiday_date)}
                  </TableCell>
                  <TableCell className="font-medium">{h.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {h.description ?? "—"}
                  </TableCell>
                  <TableCell>
                    {h.is_active ? (
                      <Badge
                        variant="outline"
                        className="border-green-200 bg-green-50 font-medium text-green-700"
                      >
                        Active
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="border-gray-200 bg-gray-50 font-medium text-gray-700"
                      >
                        Inactive
                      </Badge>
                    )}
                  </TableCell>
                  {isHrAdmin && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <HolidayFormDialog existing={h} />
                        {h.is_active && <HolidayDeleteButton holiday={h} />}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
